'use client';

import { type JSX, useCallback, useRef } from 'react';

import { Label, TextAreaInput, TextAreaInputProps } from '@videoblocks/design-system';

import { SuggestionRow } from './SuggestionRow';

export type Suggestion = { label: string; promptText: string };

export type TextAreaInputFieldProps = TextAreaInputProps & {
    suggestions?: Suggestion[];
    onSuggestionClick?: (suggestion: Suggestion) => void;
};

export const TextAreaInputField = ({
    suggestions,
    onSuggestionClick,
    disabled,
    ...rest
}: TextAreaInputFieldProps): JSX.Element => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const handleSuggestionClick = useCallback(
        (suggestion: Suggestion): void => {
            onSuggestionClick?.(suggestion);
            setTimeout(() => {
                const el = textareaRef.current;
                if (!el) return;
                el.focus();
                el.setSelectionRange(el.value.length, el.value.length);
            }, 0);
        },
        [onSuggestionClick],
    );

    const hasSuggestions = suggestions && suggestions.length > 0;

    const textAreaInput = (
        <TextAreaInput
            ref={textareaRef}
            showCount
            textAreaClassName="min-w-0 field-sizing-fixed"
            resize="none"
            id="text-prompt"
            name="text-prompt"
            disabled={disabled}
            {...rest}
        />
    );

    return (
        <div className="flex flex-col gap-2 w-full">
            <Label htmlFor="text-prompt" className="text-left" fontSize="sm">
                Prompt
            </Label>
            {hasSuggestions ? (
                <div className="dark:bg-neutral-dark-200 rounded-md flex flex-col gap-1 w-full">
                    <SuggestionRow
                        suggestions={suggestions}
                        disabled={disabled}
                        onSuggestionClick={handleSuggestionClick}
                    />
                    {textAreaInput}
                </div>
            ) : (
                textAreaInput
            )}
        </div>
    );
};
